import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Save, Loader2, Users2, ChevronDown, AlertTriangle, History } from "lucide-react";
import api from "@/lib/api";

function TabButton({ active, onClick, children, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} className={`px-3 h-9 rounded-lg text-sm font-semibold transition-colors ${active ? "bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))]" : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"}`}>{children}</button>
  );
}

function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [open, setOpen] = useState("");
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/emails/templates"); setTemplates(r.data?.templates || []); setOverrides(r.data?.overrides || {}); }
    catch { toast.error("Could not load templates"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (key) => {
    setSaving(key);
    try {
      const body = overrides[key] || { subject: "", intro: "", enabled: true };
      await api.put(`/admin/emails/templates/${key}`, body);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  const patch = (key, field, val) => setOverrides({ ...overrides, [key]: { ...(overrides[key] || {}), [field]: val, enabled: (overrides[key]?.enabled ?? true) } });

  return (
    <div className="space-y-2" data-testid="admin-emails-templates">
      {templates.map((t) => {
        const isOpen = open === t.key;
        const o = overrides[t.key] || { subject: "", intro: "", enabled: true };
        return (
          <div key={t.key} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
            <button onClick={() => setOpen(isOpen ? "" : t.key)} className="w-full flex items-center justify-between p-4 text-left hover:bg-[hsl(var(--muted))]/50">
              <div>
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.hint}</div>
              </div>
              <div className="flex items-center gap-2">
                {o.subject && <span className="inline-flex px-2 py-0.5 rounded-full bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))] text-[10px] font-semibold uppercase tracking-wide">Overridden</span>}
                <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </div>
            </button>
            {isOpen && (
              <div className="px-4 pb-4 border-t border-[hsl(var(--border))]">
                <label className="block mt-3 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Subject override</label>
                <input data-testid={`tmpl-subject-${t.key}`} value={o.subject || ""} onChange={(e) => patch(t.key, "subject", e.target.value)} placeholder="Leave blank to keep default" className="mt-1 w-full h-10 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
                <label className="block mt-3 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Intro override (HTML allowed)</label>
                <textarea data-testid={`tmpl-intro-${t.key}`} rows={4} value={o.intro || ""} onChange={(e) => patch(t.key, "intro", e.target.value)} placeholder="Leave blank to keep default. Use {{name}} to personalize." className="mt-1 w-full px-3 py-2 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
                <div className="mt-3 flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm select-none cursor-pointer">
                    <input type="checkbox" checked={o.enabled !== false} onChange={(e) => patch(t.key, "enabled", e.target.checked)} className="h-4 w-4 rounded border-[hsl(var(--input))] text-[hsl(var(--accent))]" />
                    <span>Template enabled</span>
                  </label>
                  <div className="flex-1" />
                  <button data-testid={`tmpl-save-${t.key}`} onClick={() => save(t.key)} disabled={saving === t.key} className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm disabled:opacity-60">
                    {saving === t.key ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save override
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {templates.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))]">No templates registered.</p>}
    </div>
  );
}

function BroadcastTab() {
  const [form, setForm] = useState({ audience: "all", plan: "", event_type: "", subject: "", intro_html: "", dry_run: true });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try { const r = await api.get("/admin/emails/broadcasts"); setHistory(r.data?.broadcasts || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const doPreview = async () => {
    try {
      const body = { ...form, plan: form.plan || null, event_type: form.event_type || null };
      const r = await api.post("/admin/emails/broadcast/preview", body);
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const body = { ...form, plan: form.plan || null, event_type: form.event_type || null, dry_run: false };
      const r = await api.post("/admin/emails/broadcast/send", body);
      toast.success(`Sent ${r.data?.broadcast?.sent_count || 0}, failed ${r.data?.broadcast?.failed_count || 0}`);
      setConfirm(false);
      setForm({ ...form, subject: "", intro_html: "" });
      setPreview(null);
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6" data-testid="admin-emails-broadcast">
      <div className="space-y-3">
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Audience</label>
              <select data-testid="broadcast-audience" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="w-full h-10 px-2 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm">
                <option value="all">All users</option>
                <option value="paid">Paid users (Pro / Keepsake / Legacy)</option>
                <option value="free">Free users</option>
                <option value="plan">Specific plan</option>
                <option value="event_type">By event type</option>
              </select>
            </div>
            {form.audience === "plan" && (
              <div><label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Plan</label>
                <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full h-10 px-2 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm">
                  <option value="">Select…</option><option value="free">Free</option><option value="keepsake">Keepsake</option><option value="legacy">Legacy</option><option value="pro">Pro</option>
                </select>
              </div>
            )}
            {form.audience === "event_type" && (
              <div><label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Event type</label>
                <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="w-full h-10 px-2 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm">
                  <option value="">Select…</option><option value="memorial">Memorial</option><option value="birthday">Birthday</option><option value="wedding">Wedding</option><option value="anniversary">Anniversary</option><option value="retirement">Retirement</option><option value="graduation">Graduation</option><option value="farewell">Farewell</option><option value="other">Other</option>
                </select>
              </div>
            )}
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Subject</label>
            <input data-testid="broadcast-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="A gentle update from GatherVoices" className="w-full h-10 px-3 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1">Body (HTML allowed · use {`{{name}}`} to personalize)</label>
            <textarea data-testid="broadcast-body" rows={8} value={form.intro_html} onChange={(e) => setForm({ ...form, intro_html: e.target.value })} placeholder="Hello {{name}}, we just released…" className="w-full px-3 py-2 rounded-lg border border-[hsl(var(--input))] bg-[hsl(var(--background))] text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="broadcast-preview" onClick={doPreview} className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg border border-[hsl(var(--border))] font-semibold text-sm hover:bg-[hsl(var(--muted))]"><Users2 size={14} /> Preview audience</button>
            <button data-testid="broadcast-send" onClick={() => setConfirm(true)} disabled={!form.subject.trim() || !form.intro_html.trim()} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm disabled:opacity-50"><Send size={14} /> Send broadcast</button>
          </div>
        </div>

        {preview && (
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="broadcast-preview-result">
            <div className="text-sm font-semibold">Would send to <span className="text-[hsl(var(--accent))]">{preview.total}</span> recipient{preview.total === 1 ? "" : "s"} (capped at 500 per send).</div>
            <ul className="mt-2 divide-y divide-[hsl(var(--border))] text-xs">
              {(preview.sample || []).map((s, i) => (
                <li key={i} className="py-1.5 flex items-center gap-2"><span className="font-mono">{s.email}</span>{s.name && <span className="text-[hsl(var(--muted-foreground))]">· {s.name}</span>}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="broadcast-history">
          <div className="flex items-center gap-2 text-sm font-semibold"><History size={14} /> Recent broadcasts</div>
          <ul className="mt-2 divide-y divide-[hsl(var(--border))]">
            {history.length === 0 ? <li className="py-2 text-xs text-[hsl(var(--muted-foreground))]">No broadcasts yet.</li>
            : history.map((h) => (
              <li key={h.broadcast_id} className="py-2 text-xs">
                <div className="font-semibold truncate">{h.subject}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{h.sent_count}/{h.total_target} sent · {h.audience_filter?.audience || "all"} · {new Date(h.sent_at).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-4 text-xs text-[hsl(var(--muted-foreground))] flex items-start gap-2">
          <AlertTriangle size={13} className="mt-0.5 shrink-0 text-[hsl(var(--accent))]" />
          Broadcasts respect a hard cap of 500 recipients per send. Every send is logged in the audit trail with your email.
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/60" onClick={() => setConfirm(false)}>
          <div className="w-full max-w-md rounded-2xl bg-[hsl(var(--card))] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2"><Send size={16} className="text-[hsl(var(--accent))]" /><h3 className="font-display font-semibold">Send broadcast?</h3></div>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">&ldquo;{form.subject}&rdquo; will be delivered to up to {preview?.total ?? "?"} recipients. This action is irreversible.</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => setConfirm(false)} className="flex-1 h-10 rounded-xl border border-[hsl(var(--border))] font-semibold text-sm hover:bg-[hsl(var(--muted))]">Cancel</button>
              <button data-testid="broadcast-confirm" onClick={doSend} disabled={sending} className="flex-1 h-10 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold text-sm disabled:opacity-60 inline-flex items-center justify-center gap-1.5">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminEmails() {
  const [tab, setTab] = useState("templates");
  return (
    <div className="p-6 sm:p-8 max-w-6xl" data-testid="admin-emails">
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Mail size={20} className="text-[hsl(var(--accent))]" /> Emails</h1>
      <p className="text-sm text-[hsl(var(--muted-foreground))]">Override subject lines and intro copy for transactional emails, or send a one-off broadcast to a specific audience.</p>

      <div className="mt-5 flex gap-1 border-b border-[hsl(var(--border))] pb-1">
        <TabButton testid="admin-emails-tab-templates" active={tab === "templates"} onClick={() => setTab("templates")}>Templates</TabButton>
        <TabButton testid="admin-emails-tab-broadcast" active={tab === "broadcast"} onClick={() => setTab("broadcast")}>Broadcast</TabButton>
      </div>
      <div className="mt-5">
        {tab === "templates" ? <TemplatesTab /> : <BroadcastTab />}
      </div>
    </div>
  );
}
